import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateCrewUserRequest,
    CrewUser,
    CrewUsersRequest,
    CrewUsersResponse,
    CsvUploadStateResponse,
    UpdateCrewUserRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/crew-users/api/crewUsers.api.ts';
import { deleteApiCrewUser } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/crew-users/api/crewUsers.api.ts';
import {
    createApiCrewUser,
    downloadApiCrewUsersCsv,
    downloadApiCrewUsersExampleCsv,
    getApiCrewUsers,
    getApiCrewUsersCsvUpload,
    updateApiCrewUser,
    uploadApiCrewUsersCsv,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/crew-users/api/crewUsers.api.ts';
import { CREW_USERS_STEP_TITLE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/crew-users/constants.ts';
import type { CrewUsersRow } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/crew-users/ui/table/types.ts';
import { CSV_UPLOAD_STATUS } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/constants.ts';
import { setPublishedSteps } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/model/configuration.slice.ts';
import { downloadFromDisposition } from '@utils/download.ts';
import { extractError } from '@utils/slice.ts';

export interface CrewUsersError {
    message?: string;
    [k: string]: any;
}

export interface CrewUsersState {
    error: CrewUsersError | null;
    isCsvImportErroneous: boolean;
    isCsvImportFailed: boolean;
    uploadedCsvFilename: string;
    isUploadCsvModalOpened: boolean;
    isDeleteCrewUserModalOpened: boolean;
    isActivateCrewUserModalOpened: boolean;
    isDeactivateCrewUserModalOpened: boolean;
    uploadedCsvFile: File | null;
    uploadedCsvId: string | null;
    csvUploadState: CsvUploadStateResponse | null;

    commonCrewUsers: CrewUsersResponse | null;
    crewUsers: CrewUser[];
    pagination: CommonTablePaginatedResponse;
    updatedCrewUser: CrewUser | null;
    selectedCrewUser: CrewUsersRow | null;
    selectedRowInitialData: CrewUsersRow | null;
    currentGetRequestId: string | null;

    isGetCrewUsersPending: boolean;
    isCreateCrewUserPending: boolean;
    isUpdateCrewUserPending: boolean;
    isUploadCsvPending: boolean;
    isGetCsvUploadPending: boolean;
    isDownloadCsvPending: boolean;
    isGetExampleCsvPending: boolean;
    isDeleteCrewUserPending: boolean;
}

const initialState: CrewUsersState = {
    error: null,
    isCsvImportErroneous: false,
    isCsvImportFailed: false,
    uploadedCsvFilename: '',
    isUploadCsvModalOpened: false,
    uploadedCsvFile: null,
    uploadedCsvId: null,
    csvUploadState: null,

    commonCrewUsers: null,
    crewUsers: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },
    updatedCrewUser: null,
    selectedCrewUser: null,
    selectedRowInitialData: null,
    currentGetRequestId: null,

    isGetCrewUsersPending: false,
    isCreateCrewUserPending: false,
    isUpdateCrewUserPending: false,
    isUploadCsvPending: false,
    isGetCsvUploadPending: false,
    isDownloadCsvPending: false,
    isGetExampleCsvPending: false,
    isDeleteCrewUserPending: false,
    isDeleteCrewUserModalOpened: false,
    isActivateCrewUserModalOpened: false,
    isDeactivateCrewUserModalOpened: false,
};

export const getCrewUsers = createAsyncThunk<
    CrewUsersResponse,
    { clientId: number; tenantAlias: string; payload: CrewUsersRequest },
    { rejectValue: CrewUsersError }
>('adminCenterCrewUsers/getCrewUsers', ({ clientId, tenantAlias, payload }, { rejectWithValue, dispatch }) =>
    getApiCrewUsers(clientId, tenantAlias, payload)
        .then((res) => {
            if (res.data?.length > 0) {
                dispatch(setPublishedSteps(CREW_USERS_STEP_TITLE));
            }
            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

export const createCrewUser = createAsyncThunk<
    CrewUser,
    { clientId: number; tenantAlias: string; payload: CreateCrewUserRequest },
    { rejectValue: CrewUsersError }
>('adminCenterCrewUsers/createCrewUser', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiCrewUser(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateCrewUser = createAsyncThunk<
    CrewUser,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateCrewUserRequest },
    { rejectValue: CrewUsersError }
>('adminCenterCrewUsers/updateCrewUser', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiCrewUser(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteCrewUser = createAsyncThunk<
    CrewUser,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: CrewUsersError }
>('adminCenterCrewUsers/deleteCrewUser', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiCrewUser(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const uploadCrewUsersCsv = createAsyncThunk<
    string,
    { clientId: number; tenantAlias: string; formData: FormData },
    { rejectValue: CrewUsersError }
>('adminCenterCrewUsers/uploadCrewUsersCsv', ({ clientId, tenantAlias, formData }, { rejectWithValue }) =>
    uploadApiCrewUsersCsv(clientId, tenantAlias, formData)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getCrewUsersCsvUpload = createAsyncThunk<
    CsvUploadStateResponse,
    { clientId: number; tenantAlias: string; id: string },
    { rejectValue: CrewUsersError }
>('adminCenterCrewUsers/getCrewUsersCsvUpload', ({ clientId, tenantAlias, id }, { rejectWithValue, dispatch }) =>
    getApiCrewUsersCsvUpload(clientId, tenantAlias, id)
        .then((res) => {
            if (res.uploadStatus === CSV_UPLOAD_STATUS.SUCCESS) {
                dispatch(setPublishedSteps(CREW_USERS_STEP_TITLE));
            }

            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

export const downloadCrewUsersCsv = createAsyncThunk<
    void,
    { clientId: number; tenantAlias: string },
    { rejectValue: CrewUsersError }
>('adminCenterCrewUsers/downloadCrewUsersCsv', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    downloadApiCrewUsersCsv(clientId, tenantAlias)
        .then((res) => {
            downloadFromDisposition(res);
        })
        .catch((err) => rejectWithValue(err)),
);

export const downloadCrewUsersExampleCsv = createAsyncThunk<
    void,
    { clientId: number; tenantAlias: string },
    { rejectValue: CrewUsersError }
>('adminCenterCrewUsers/downloadCrewUsersExampleCsv', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    downloadApiCrewUsersExampleCsv(clientId, tenantAlias)
        .then((res) => {
            downloadFromDisposition(res);
        })
        .catch((err) => rejectWithValue(err)),
);

const crewUsersSlice = createSlice({
    name: 'adminCenterCrewUsers',
    initialState,
    reducers: {
        setIsCsvImportErroneous(state, action: PayloadAction<boolean>) {
            state.isCsvImportErroneous = action.payload;
        },
        setIsCsvImportFailed(state, action: PayloadAction<boolean>) {
            state.isCsvImportFailed = action.payload;
        },
        setUploadedCsvFilename(state, action: PayloadAction<string>) {
            state.uploadedCsvFilename = action.payload;
        },
        setIsUploadCsvModalOpened(state, action: PayloadAction<boolean>) {
            state.isUploadCsvModalOpened = action.payload;
        },
        setUploadedCsvFile(state, action: PayloadAction<File | null>) {
            state.uploadedCsvFile = action.payload;
        },
        setCsvUploadState(state, action: PayloadAction<CsvUploadStateResponse | null>) {
            state.csvUploadState = action.payload;
        },
        clearCrewUsersState(state) {
            state.isCsvImportErroneous = false;
            state.isCsvImportFailed = false;
            state.csvUploadState = null;
            state.uploadedCsvFilename = '';
            state.uploadedCsvFile = null;
            state.uploadedCsvId = null;
        },

        setIsDeleteCrewUserModalOpened(state, action: PayloadAction<boolean>) {
            state.isDeleteCrewUserModalOpened = action.payload;
        },
        setIsActivateCrewUserModalOpened(state, action: PayloadAction<boolean>) {
            state.isActivateCrewUserModalOpened = action.payload;
        },
        setIsDeactivateCrewUserModalOpened(state, action: PayloadAction<boolean>) {
            state.isDeactivateCrewUserModalOpened = action.payload;
        },
        setSelectedCrewUser(state, action: PayloadAction<CrewUsersRow | null>) {
            state.selectedCrewUser = action.payload;
        },
        setSelectedRowInitialData(state, action: PayloadAction<CrewUsersRow | null>) {
            state.selectedRowInitialData = action.payload;
        },
        setCrewUsers(state, action: PayloadAction<CrewUser[]>) {
            state.crewUsers = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getCrewUsers.pending, (state, action) => {
            state.isGetCrewUsersPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getCrewUsers.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetCrewUsersPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonCrewUsers = action.payload;
            state.crewUsers = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getCrewUsers.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetCrewUsersPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createCrewUser.pending, (state) => {
            state.isCreateCrewUserPending = true;
            state.error = null;
        });
        builder.addCase(createCrewUser.fulfilled, (state, action) => {
            state.isCreateCrewUserPending = false;
            state.updatedCrewUser = action.payload;
        });
        builder.addCase(createCrewUser.rejected, (state, action) => {
            state.isCreateCrewUserPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateCrewUser.pending, (state) => {
            state.isUpdateCrewUserPending = true;
            state.error = null;
        });
        builder.addCase(updateCrewUser.fulfilled, (state, action) => {
            state.isUpdateCrewUserPending = false;
            state.updatedCrewUser = action.payload;
        });
        builder.addCase(updateCrewUser.rejected, (state, action) => {
            state.isUpdateCrewUserPending = false;
            state.error = extractError(action);
        });
        builder.addCase(deleteCrewUser.pending, (state) => {
            state.isDeleteCrewUserPending = true;
            state.error = null;
        });
        builder.addCase(deleteCrewUser.fulfilled, (state, action) => {
            const deletedId = action.payload.id;

            state.isDeleteCrewUserPending = false;
            state.updatedCrewUser = action.payload;
            state.crewUsers = state.crewUsers.filter((user) => user.id !== deletedId);
        });
        builder.addCase(deleteCrewUser.rejected, (state, action) => {
            state.isDeleteCrewUserPending = false;
            state.error = extractError(action);
        });

        builder.addCase(uploadCrewUsersCsv.pending, (state) => {
            state.isUploadCsvPending = true;
            state.error = null;
            state.isCsvImportFailed = false;
            state.uploadedCsvId = null;
            state.csvUploadState = null;
        });
        builder.addCase(uploadCrewUsersCsv.fulfilled, (state, action) => {
            state.isUploadCsvPending = false;
            state.uploadedCsvId = action.payload;
            state.uploadedCsvFile = null;
        });
        builder.addCase(uploadCrewUsersCsv.rejected, (state, action) => {
            state.isUploadCsvPending = false;
            state.isCsvImportFailed = true;
            state.error = extractError(action);
        });

        builder.addCase(getCrewUsersCsvUpload.pending, (state) => {
            state.isGetCsvUploadPending = true;
            state.error = null;
            state.isCsvImportErroneous = false;
        });
        builder.addCase(getCrewUsersCsvUpload.fulfilled, (state, action) => {
            state.isGetCsvUploadPending = false;
            state.csvUploadState = action.payload;
            const { uploadStatus } = action.payload;

            if (uploadStatus === CSV_UPLOAD_STATUS.SUCCESS) {
                state.uploadedCsvId = null;
            }
        });
        builder.addCase(getCrewUsersCsvUpload.rejected, (state, action) => {
            state.isGetCsvUploadPending = false;
            state.error = extractError(action);
        });

        builder.addCase(downloadCrewUsersCsv.pending, (state) => {
            state.isDownloadCsvPending = true;
            state.error = null;
        });
        builder.addCase(downloadCrewUsersCsv.fulfilled, (state) => {
            state.isDownloadCsvPending = false;
        });
        builder.addCase(downloadCrewUsersCsv.rejected, (state, action) => {
            state.isDownloadCsvPending = false;
            state.error = extractError(action);
        });

        builder.addCase(downloadCrewUsersExampleCsv.pending, (state) => {
            state.isGetExampleCsvPending = true;
            state.error = null;
        });
        builder.addCase(downloadCrewUsersExampleCsv.fulfilled, (state) => {
            state.isGetExampleCsvPending = false;
        });
        builder.addCase(downloadCrewUsersExampleCsv.rejected, (state, action) => {
            state.isGetExampleCsvPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { crewUserResponse } = action.payload;

            if (!crewUserResponse) return;

            const { data, pagination } = crewUserResponse;
            state.commonCrewUsers = crewUserResponse;
            state.crewUsers = data ?? [];
            state.pagination = pagination;
        });
    },
});

export const {
    setIsCsvImportErroneous,
    setIsCsvImportFailed,
    setUploadedCsvFilename,
    setIsUploadCsvModalOpened,
    setUploadedCsvFile,
    setCsvUploadState,
    clearCrewUsersState,
    setIsDeleteCrewUserModalOpened,
    setIsActivateCrewUserModalOpened,
    setIsDeactivateCrewUserModalOpened,
    setSelectedCrewUser,
    setSelectedRowInitialData,
    setCrewUsers,
} = crewUsersSlice.actions;

export default crewUsersSlice.reducer;
